import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Instagram, Linkedin, Twitter, Facebook, ArrowRight, Send, Lock, FileText } from 'lucide-react';
import React from 'react';
import { Link } from 'react-router-dom';
import Logo from '../assets/dexton-logo-png.png';
import { SITE } from '../config/site';
import { servicesCatalog } from '../data/servicesCatalog';

export default function Footer() {
  const quickLinks = [
    { name: 'Home', to: '/' },
    { name: 'About', to: '/about' },
    { name: 'Services', to: '/services' },
    { name: 'Get a proposal', to: '/get-proposal' },
    { name: 'Hire Developer', to: '/hire-developers' },
    { name: 'Contact', to: '/contact' },
    { name: 'Portfolio', to: '/portfolio' },
    { name: 'Testimonials', to: '/testimonials' },
    { name: 'Blog', to: '/blog' },
    { name: 'FAQ', to: '/faq' },
    { name: 'Global HQ', to: '/locations/surat-gujarat' },
  ];

  const services = servicesCatalog.slice(0, 10).map((s) => ({
    name: s.shortTitle,
    to: `/services/${s.slug}`,
  }));

  const socialLinks = [
    { icon: <Instagram className="w-5 h-5" />, href: SITE.social.instagram, label: 'Instagram' },
    { icon: <Twitter className="w-5 h-5" />, href: SITE.social.twitter, label: 'Twitter' },
    { icon: <Facebook className="w-5 h-5" />, href: SITE.social.facebook, label: 'Facebook' },
    { icon: <Linkedin className="w-5 h-5" />, href: SITE.social.linkedin, label: 'LinkedIn' },
  ];

  const legalLinks = [
    { name: 'Privacy Policy', icon: <Lock className="w-4 h-4" />, to: '/privacy' },
    { name: 'Terms of Service', icon: <FileText className="w-4 h-4" />, to: '/terms' },
  ];

  const napLine = `${SITE.address.street}, ${SITE.address.city}, ${SITE.address.region} ${SITE.address.postalCode}`;

  return (
    <footer id="footer" className="relative overflow-hidden bg-neutral-950 text-white">
      <div className="border-b border-white/10">
        <div className="mx-auto max-w-[90rem] px-4 py-20 sm:px-6 lg:px-10 xl:px-16">
          <p className="font-display text-[clamp(2.5rem,7vw,5rem)] font-bold leading-[0.95] tracking-tight text-white">
            Let&apos;s build
            <span className="block text-primary-300">something great.</span>
          </p>
          <Link
            to="/get-proposal"
            className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-primary-300 hover:text-white"
          >
            Start a project
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary-400/70 to-transparent"
        aria-hidden
      />
      <div className="pointer-events-none absolute right-0 top-0 h-64 w-64 translate-x-1/2 -translate-y-1/4 rounded-full bg-secondary-600/20 blur-[100px]" aria-hidden />
      <div className="pointer-events-none absolute bottom-0 left-0 h-48 w-48 -translate-x-1/4 rounded-full bg-primary-500/15 blur-[90px]" aria-hidden />

      <div className="relative border-b border-white/[0.06]">
        <div className="container mx-auto flex max-w-7xl flex-col items-center justify-between gap-8 px-4 py-12 sm:px-6 sm:py-14 md:flex-row md:items-end">
          <div className="max-w-xl text-center md:text-left">
            <p className="font-display text-2xl font-bold tracking-tight md:text-3xl">Insights in your inbox.</p>
            <p className="mt-3 text-sm leading-relaxed text-zinc-400">
              Product teardowns, tech notes, and launch checklists—occasional emails, no fluff.
            </p>
          </div>
          <a
            href={`mailto:${SITE.email}?subject=Newsletter%20subscribe`}
            className="inline-flex w-full items-center justify-center rounded-2xl border border-white/20 bg-white/[0.06] px-10 py-4 text-sm font-semibold backdrop-blur-md transition hover:border-primary-400/40 hover:bg-white/10 md:w-auto"
          >
            Subscribe via email
          </a>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6">
        {/* Main Footer Content */}
        <div className="py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Company Info */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="flex items-center space-x-3">
                <img 
                  src={Logo} 
                  alt="Dexton Technology" 
                  className="h-10 w-10"
                />
                <span className="font-display text-xl font-bold">Dexton Technology</span>
              </div>
              <p className="text-neutral-300 leading-relaxed">
                We design and engineer mobile, web, and enterprise software built around how your business actually works.
              </p>
              
              {/* Social Links */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-neutral-200">Follow Us</h4>
                <div className="flex space-x-3">
                  {socialLinks.map((social, index) => (
                    <motion.a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }}
                      whileHover={{ scale: 1.1, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      className="w-10 h-10 bg-neutral-700/50 backdrop-blur-sm rounded-lg flex items-center justify-center hover:bg-primary-600 transition-all duration-300 border border-neutral-600/50 hover:border-primary-500"
                      title={social.label}
                    >
                      {social.icon}
                    </motion.a>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Quick Links */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h4 className="text-lg font-semibold text-white">Quick Links</h4>
              <ul className="space-y-3">
                {quickLinks.map((link, index) => (
                  <motion.li
                    key={link.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                  >
                    <Link
                      to={link.to}
                      className="flex items-center space-x-2 text-neutral-300 hover:text-white transition-all duration-300 group"
                    >
                      <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <span className="group-hover:translate-x-1 transition-transform duration-300">
                        {link.name}
                      </span>
                    </Link>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Services */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h4 className="text-lg font-semibold text-white">Services</h4>
              <ul className="space-y-3">
                {services.map((service, index) => (
                  <motion.li
                    key={service.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.4 + index * 0.1 }}
                  >
                    <Link
                      to={service.to}
                      className="flex items-center space-x-2 text-neutral-300 hover:text-white transition-all duration-300 group"
                    >
                      <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <span className="group-hover:translate-x-1 transition-transform duration-300">
                        {service.name}
                      </span>
                    </Link>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h4 className="text-lg font-semibold text-white">Get in Touch</h4>
              <div className="space-y-4">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.5 }}
                  className="flex items-center space-x-3 group"
                >
                  <Mail className="w-5 h-5 text-primary-400 group-hover:text-primary-300 transition-colors duration-300" />
                  <div>
                    <a
                      href={`mailto:${SITE.email}`}
                      className="text-neutral-300 hover:text-white transition-colors duration-300"
                    >
                      {SITE.email}
                    </a>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.6 }}
                  className="flex items-center space-x-3 group"
                >
                  <Phone className="w-5 h-5 text-primary-400 group-hover:text-primary-300 transition-colors duration-300" />
                  <div>
                    <a
                      href={`tel:${SITE.phoneTel}`}
                      className="text-neutral-300 hover:text-white transition-colors duration-300"
                    >
                      {SITE.phoneDisplay}
                    </a>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.7 }}
                  className="flex items-start space-x-3 group"
                >
                  <MapPin className="w-5 h-5 text-primary-400 group-hover:text-primary-300 transition-colors duration-300 mt-1 flex-shrink-0" />
                  <address className="text-neutral-300 leading-relaxed not-italic">
                    <a href={SITE.googleBusinessUrl} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                      {napLine}
                    </a>
                  </address>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
          className="border-t border-neutral-700 py-12"
        >
          <div className="bg-gradient-to-r from-primary-600 to-secondary-600 rounded-2xl p-8 lg:p-12 text-center">
            <h3 className="text-2xl font-bold text-white mb-4">
              Ready to Start Your Project?
            </h3>
            <p className="text-primary-100 mb-6 max-w-2xl mx-auto">
              Let's discuss how we can help bring your vision to life with our expertise and innovative solutions.
            </p>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="flex flex-wrap justify-center gap-3"
            >
              <Link
                to="/contact"
                className="inline-flex items-center px-8 py-4 bg-white text-primary-600 font-semibold rounded-xl hover:bg-primary-50 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <Send className="w-5 h-5 mr-2" />
                Get Started Today
              </Link>
              <a
                href={SITE.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-8 py-4 rounded-xl border-2 border-white/80 text-white font-semibold hover:bg-white/10 transition-all"
              >
                WhatsApp
              </a>
            </motion.div>
          </div>
        </motion.div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
          className="border-t border-neutral-700 py-6"
        >
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-neutral-400 text-sm">
              © {new Date().getFullYear()} Dexton Technology. All rights reserved.
            </div>
            <div className="flex flex-wrap justify-center gap-6 md:justify-end">
              {legalLinks.map((link, index) => (
                <motion.span
                  key={link.name}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.8 + index * 0.1 }}
                >
                  <Link
                    to={link.to}
                    className="flex items-center space-x-1 text-neutral-400 hover:text-white transition-all duration-300 text-sm"
                  >
                    {link.icon}
                    <span>{link.name}</span>
                  </Link>
                </motion.span>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  );
}
